/* ============================================================
   AEGEAN TABLE — script.js
   Custom cursor · Header · Scroll reveal · 3D tilt · Filters
   Cart · Wishlist · Mobile menu · Toast · Smooth scroll
   ============================================================ */

'use strict';

/* ── Cart State ────────────────────────────────────────────── */
let cartItems = [];

/* ══════════════════════════════════════════════════════════════
   1. CUSTOM CURSOR
══════════════════════════════════════════════════════════════ */
(function initCursor() {
  const dot  = document.getElementById('cursor');
  const ring = document.getElementById('cursorFollower');
  if (!dot || !ring) return;

  let mx = -200, my = -200;
  let rx = -200, ry = -200;

  document.addEventListener('mousemove', e => {
    mx = e.clientX;
    my = e.clientY;
    dot.style.left = mx + 'px';
    dot.style.top  = my + 'px';
  });

  /* Smooth ring follow */
  (function animateRing() {
    rx += (mx - rx) * 0.1;
    ry += (my - ry) * 0.1;
    ring.style.left = rx + 'px';
    ring.style.top  = ry + 'px';
    requestAnimationFrame(animateRing);
  })();

  /* Enlarge on interactive elements */
  const sel = 'a, button, .product-card, .category-card, .filter-btn, input';
  document.querySelectorAll(sel).forEach(el => {
    el.addEventListener('mouseenter', () => ring.classList.add('hover'));
    el.addEventListener('mouseleave', () => ring.classList.remove('hover'));
  });

  /* Hide on touch devices */
  document.addEventListener('touchstart', () => {
    dot.style.display  = 'none';
    ring.style.display = 'none';
  }, { once: true });
})();

/* ══════════════════════════════════════════════════════════════
   2. STICKY HEADER  (scroll-shadow + hide on scroll-down)
══════════════════════════════════════════════════════════════ */
(function initHeader() {
  const header = document.getElementById('header');
  if (!header) return;

  let lastY = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    header.classList.toggle('scrolled', y > 60);
    header.style.transform = (y > lastY && y > 200) ? 'translateY(-100%)' : 'translateY(0)';
    lastY = y;
  }, { passive: true });
})();

/* ══════════════════════════════════════════════════════════════
   3. SCROLL REVEAL
══════════════════════════════════════════════════════════════ */
(function initScrollReveal() {
  /* Mark all reveal targets */
  /* Add scroll-reveal to extra elements not already tagged in HTML */
  const autoReveal = document.querySelectorAll(
    '.section-label, .section-title, .section-sub, ' +
    '.product-card, .cat-card, .category-card, .review-card, ' +
    '.delivery-item, .step, .why-item, .story-stat, ' +
    '.footer-col, .reveal'
  );
  autoReveal.forEach(el => el.classList.add('scroll-reveal'));

  /* Observe ALL .scroll-reveal elements (pre-existing + newly added) */
  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      /* Stagger siblings */
      const siblings = [...(entry.target.parentElement?.children || [])].filter(
        c => c.classList.contains('scroll-reveal')
      );
      const idx = siblings.indexOf(entry.target);
      entry.target.style.transitionDelay = `${idx * 70}ms`;
      entry.target.classList.add('visible');
      io.unobserve(entry.target);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.scroll-reveal').forEach(el => io.observe(el));
})();

/* ══════════════════════════════════════════════════════════════
   4. 3D CARD TILT
══════════════════════════════════════════════════════════════ */
(function initTilt() {
  document.querySelectorAll('.product-card').forEach(card => {
    card.addEventListener('mousemove', e => {
      const r  = card.getBoundingClientRect();
      const dx = (e.clientX - r.left  - r.width  / 2) / (r.width  / 2);
      const dy = (e.clientY - r.top   - r.height / 2) / (r.height / 2);
      card.style.transform = `perspective(700px) rotateY(${dx * 7}deg) rotateX(${-dy * 7}deg) translateY(-6px)`;
    });
    card.addEventListener('mouseleave', () => { card.style.transform = ''; });
  });
})();

/* ══════════════════════════════════════════════════════════════
   5. PRODUCT FILTERS + SEARCH
══════════════════════════════════════════════════════════════ */
(function initFilters() {
  const filterBtns  = document.querySelectorAll('.filter-btn');
  const productCards = document.querySelectorAll('.product-card[data-category]');
  const searchInput  = document.querySelector('.search-box input, .search-input');
  let activeFilter   = 'all';

  function applyFilter() {
    const q = searchInput ? searchInput.value.trim().toLowerCase() : '';
    let visible = 0;

    productCards.forEach((card, i) => {
      const cat  = card.dataset.category || '';
      const name = (card.dataset.name || card.querySelector('.product-name')?.textContent || '').toLowerCase();
      const show = (activeFilter === 'all' || cat === activeFilter) && (!q || name.includes(q));

      card.style.display = show ? '' : 'none';
      if (show) {
        card.style.animationDelay = `${visible * 60}ms`;
        card.classList.remove('fade-in');
        void card.offsetWidth; /* reflow */
        card.classList.add('fade-in');
        visible++;
      }
    });
  }

  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      filterBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      activeFilter = btn.dataset.filter || 'all';
      applyFilter();
    });
  });

  if (searchInput) searchInput.addEventListener('input', applyFilter);
})();

/* ══════════════════════════════════════════════════════════════
   6. CART
══════════════════════════════════════════════════════════════ */
function toggleCart() {
  const sidebar = document.getElementById('cartSidebar');
  const overlay = document.getElementById('cartOverlay');
  if (!sidebar || !overlay) return;
  const isOpen  = sidebar.classList.contains('open');

  if (isOpen) {
    sidebar.classList.remove('open');
    overlay.classList.remove('active');
    document.body.style.overflow = '';
  } else {
    refreshCart();
    sidebar.classList.add('open');
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
}

function money(cents) {
  return 'CHF ' + ((cents || 0) / 100).toFixed(2);
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

async function getShopifyCart() {
  const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
  if (!res.ok) throw new Error('Cart fetch failed');
  return res.json();
}

async function refreshCart() {
  try {
    const cart = await getShopifyCart();
    cartItems = cart.items || [];
    updateCartCount(cart);
    renderCartItems(cart);
    return cart;
  } catch (err) {
    showToast('Cart could not be loaded');
    return null;
  }
}

async function addVariantToCart(variantId, qty = 1) {
  if (!variantId) return;

  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: Number(variantId), quantity: Number(qty) || 1 })
  });

  if (!res.ok) {
    showToast('This product could not be added to cart');
    return;
  }

  const added = await res.json().catch(() => null);
  const cart = await refreshCart();

  document.getElementById('cartSidebar')?.classList.add('open');
  document.getElementById('cartOverlay')?.classList.add('active');
  document.body.style.overflow = 'hidden';

  showToast('<strong>' + escapeHtml(added?.product_title || 'Product') + '</strong> added to cart');
  return cart;
}

function addToCart(btn) {
  const card = btn.closest('.product-card, [data-variant-id]');
  if (!card) return;
  addVariantToCart(card.dataset.variantId, 1);
}

async function updateCartLine(key, qty) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity: Math.max(0, Number(qty) || 0) })
  });
  if (!res.ok) {
    showToast('Cart could not be updated');
    return;
  }
  refreshCart();
}

function removeCartItem(key) {
  updateCartLine(key, 0);
}

function changeQty(key, delta) {
  const item = cartItems.find(i => i.key === key);
  if (!item) return;
  updateCartLine(key, item.quantity + delta);
}

function updateCartCount(cart) {
  const count = cart ? cart.item_count : cartItems.reduce((a, i) => a + (i.quantity || 0), 0);
  const el    = document.getElementById('cartCount');
  if (el) {
    el.textContent = count;
    el.classList.add('bump');
    setTimeout(() => el.classList.remove('bump'), 350);
  }
}

function renderCartItems(cart) {
  const list   = document.getElementById('cartItems');
  const footer = document.getElementById('cartFooter');
  const total  = document.getElementById('cartTotal');
  if (!list) return;

  const items = cart ? (cart.items || []) : cartItems;

  if (!items.length) {
    list.innerHTML = '<div class="cart-empty">' +
      '<div style="font-size:3.5rem;margin-bottom:12px">??</div>' +
      '<p style="font-size:1.1rem;font-weight:600">Your cart is empty</p>' +
      '<p style="opacity:0.6;font-size:0.88rem;margin-top:6px">Add some Greek treasures!</p>' +
      '</div>';
    if (footer) footer.style.display = 'none';
    return;
  }

  list.innerHTML = items.map(function (item) {
    return '<div class="cart-item-row">' +
      '<div class="cart-item-emoji">' + (item.image ? '<img src="' + item.image + '" alt="" style="width:42px;height:42px;object-fit:cover;border-radius:10px">' : '??') + '</div>' +
      '<div class="cart-item-info">' +
        '<div class="cart-item-name">' + escapeHtml(item.product_title || item.title) + '</div>' +
        '<div class="cart-item-price">' + money(item.final_line_price) + '</div>' +
      '</div>' +
      '<div class="cart-item-qty">' +
        '<button onclick="changeQty(\'' + item.key + '\',-1)">?</button>' +
        '<span>' + item.quantity + '</span>' +
        '<button onclick="changeQty(\'' + item.key + '\',1)">+</button>' +
      '</div>' +
      '<button class="cart-item-del" onclick="removeCartItem(\'' + item.key + '\')" title="Remove">?</button>' +
    '</div>';
  }).join('');

  if (footer) footer.style.display = '';
  if (total) total.textContent = money(cart ? cart.total_price : items.reduce((a,i)=>a + (i.final_line_price || 0), 0));
}

/* Open cart from header button */
document.getElementById('cartBtn')?.addEventListener('click', toggleCart);
refreshCart();

/* ══════════════════════════════════════════════════════════════
   7. WISHLIST
══════════════════════════════════════════════════════════════ */
(function initWishlist() {
  document.querySelectorAll('.product-wish').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const isActive = btn.classList.toggle('active');
      btn.textContent = isActive ? '♥' : '♡';
      const name = btn.closest('.product-card')?.querySelector('.product-name')?.textContent.trim() || 'Product';
      showToast(isActive ? `❤️ <strong>${name}</strong> saved to wishlist` : `Removed from wishlist`);
    });
  });
})();

/* ══════════════════════════════════════════════════════════════
   8. MOBILE BURGER MENU
══════════════════════════════════════════════════════════════ */
(function initMobileMenu() {
  const burger  = document.getElementById('burger');
  const overlay = document.getElementById('atMobNav');
  if (!burger || !overlay) return;

  function openNav() {
    burger.classList.add('open');
    overlay.classList.add('open');
    overlay.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }
  function closeNav() {
    burger.classList.remove('open');
    overlay.classList.remove('open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  burger.addEventListener('click', function () {
    if (burger.classList.contains('open')) closeNav(); else openNav();
  });

  var closeBtn = document.getElementById('atMobClose');
  if (closeBtn) closeBtn.addEventListener('click', closeNav);

  /* Close on any nav link tap */
  overlay.querySelectorAll('a').forEach(function (a) {
    a.addEventListener('click', closeNav);
  });
})();

/* ══════════════════════════════════════════════════════════════
   9. SMOOTH SCROLL
══════════════════════════════════════════════════════════════ */
document.querySelectorAll('a[href^="#"]').forEach(link => {
  link.addEventListener('click', e => {
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    const offset = (document.getElementById('header')?.offsetHeight || 80) + 16;
    window.scrollTo({ top: target.getBoundingClientRect().top + scrollY - offset, behavior: 'smooth' });
  });
});

/* ══════════════════════════════════════════════════════════════
   10. HERO PARALLAX
══════════════════════════════════════════════════════════════ */
(function initParallax() {
  const orbs = document.querySelectorAll('.hero-orb');
  if (!orbs.length) return;
  window.addEventListener('scroll', () => {
    const y = scrollY;
    orbs.forEach((orb, i) => {
      orb.style.transform = `translateY(${y * (0.05 + i * 0.03)}px)`;
    });
  }, { passive: true });
})();

/* ══════════════════════════════════════════════════════════════
   11. ANIMATED COUNTERS
══════════════════════════════════════════════════════════════ */
(function initCounters() {
  const counters = document.querySelectorAll('.stat-value, .hero-stat strong, [data-count]');
  if (!counters.length) return;

  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el      = entry.target;
      const text    = el.textContent.trim();
      const end     = parseFloat(text.replace(/[^0-9.]/g, ''));
      const suffix  = text.replace(/[0-9.]/g, '').trim();
      if (!end) return;

      const dur = 1500;
      const t0  = performance.now();
      (function tick(now) {
        const p = Math.min((now - t0) / dur, 1);
        const v = 1 - Math.pow(1 - p, 3); /* ease-out cubic */
        el.textContent = (end < 10 ? (v * end).toFixed(1) : Math.floor(v * end)) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      })(t0);

      io.unobserve(el);
    });
  }, { threshold: 0.6 });

  counters.forEach(el => io.observe(el));
})();

/* ══════════════════════════════════════════════════════════════
   12. TOAST
══════════════════════════════════════════════════════════════ */
function showToast(msg, duration = 3000) {
  /* Use existing #toast or create floating ones */
  let el = document.getElementById('toast');

  /* If the built-in toast exists, use it simply */
  if (el) {
    el.innerHTML = msg;
    el.classList.add('show');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('show'), duration);
    return;
  }

  /* Otherwise create a floating toast */
  let container = document.querySelector('.toast-wrap');
  if (!container) {
    container = document.createElement('div');
    container.className = 'toast-wrap';
    document.body.appendChild(container);
  }
  const t = document.createElement('div');
  t.className = 'toast-float';
  t.innerHTML = msg;
  container.appendChild(t);
  requestAnimationFrame(() => t.classList.add('show'));
  setTimeout(() => {
    t.classList.remove('show');
    t.addEventListener('transitionend', () => t.remove(), { once: true });
  }, duration);
}

/* ══════════════════════════════════════════════════════════════
   13. KEYBOARD  (Escape closes overlays)
══════════════════════════════════════════════════════════════ */
/* ── Global cart API: lets the product-detail page add items ─ */
window.atCartAddItem = function (variantId, qty) {
  return addVariantToCart(variantId, qty || 1);
};

document.addEventListener('keydown', e => {
  if (e.key !== 'Escape') return;
  const sidebar = document.getElementById('cartSidebar');
  if (sidebar?.classList.contains('open')) toggleCart();
  const mobNav = document.getElementById('atMobNav');
  if (mobNav?.classList.contains('open')) {
    document.getElementById('burger')?.classList.remove('open');
    mobNav.classList.remove('open');
    mobNav.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
});

/* ══════════════════════════════════════════════════════════════
   14. INJECT SUPPORTING STYLES  (cart rows, toast, cursor hover,
       scroll-reveal, burger open, counter badge bump)
══════════════════════════════════════════════════════════════ */
(function injectStyles() {
  const css = `
    /* ── Scroll reveal ── */
    .scroll-reveal { opacity:0; transform:translateY(28px); transition:opacity .6s ease,transform .6s ease; }
    .scroll-reveal.visible { opacity:1; transform:none; }

    /* ── Filter fade-in ── */
    @keyframes fadeSlideIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
    .product-card.fade-in { animation:fadeSlideIn .35s ease both; }

    /* ── Header transition ── */
    .header { transition:transform .35s ease,background .35s ease,box-shadow .35s ease; }
    .header.scrolled { background:rgba(12,26,16,0.97) !important; box-shadow:0 2px 20px rgba(0,0,0,.5); }

    /* ── Cart overlay ── */
    #cartOverlay { position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1999;opacity:0;pointer-events:none;transition:opacity .3s; }
    #cartOverlay.active { opacity:1;pointer-events:all; }

    /* ── Cart sidebar open ── */
    #cartSidebar { transition:transform .4s cubic-bezier(.4,0,.2,1); }
    #cartSidebar.open { transform:translateX(0) !important; }

    /* ── Cart rows ── */
    .cart-item-row { display:flex;align-items:center;gap:10px;padding:14px 0;border-bottom:1px solid rgba(255,255,255,.08); }
    .cart-item-emoji { font-size:2rem;width:42px;text-align:center;flex-shrink:0; }
    .cart-item-info  { flex:1;min-width:0; }
    .cart-item-name  { font-size:.9rem;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
    .cart-item-price { color:#c8a84b;font-size:.84rem;margin-top:3px; }
    .cart-item-qty   { display:flex;align-items:center;gap:6px; }
    .cart-item-qty button {
      width:26px;height:26px;border-radius:50%;border:1px solid rgba(255,255,255,.2);
      background:transparent;color:#fff;cursor:pointer;font-size:1rem;line-height:1;
      display:flex;align-items:center;justify-content:center;transition:.2s;
    }
    .cart-item-qty button:hover { background:#c8a84b;border-color:#c8a84b;color:#0c1a10; }
    .cart-item-del {
      width:24px;height:24px;border:none;background:rgba(255,80,80,.15);color:#ff6b6b;
      border-radius:50%;cursor:pointer;font-size:1rem;line-height:1;
      display:flex;align-items:center;justify-content:center;transition:.2s;
    }
    .cart-item-del:hover { background:#ff6b6b;color:#fff; }
    .cart-empty { text-align:center;padding:48px 24px;opacity:.7; }

    /* ── Cart count bump ── */
    @keyframes bump { 0%,100%{transform:scale(1)} 50%{transform:scale(1.5)} }
    .cart-count.bump { animation:bump .35s ease; }

    /* ── Toast (built-in #toast) ── */
    #toast {
      position:fixed;bottom:32px;left:50%;transform:translateX(-50%) translateY(20px);
      background:rgba(18,30,20,.97);color:#fff;padding:13px 24px;border-radius:40px;
      border:1px solid rgba(200,168,75,.4);backdrop-filter:blur(12px);
      font-size:.9rem;z-index:9999;opacity:0;pointer-events:none;
      transition:opacity .3s,transform .3s cubic-bezier(.34,1.56,.64,1);white-space:nowrap;
    }
    #toast.show { opacity:1;transform:translateX(-50%) translateY(0); }

    /* ── Floating toasts ── */
    .toast-wrap { position:fixed;bottom:32px;left:50%;transform:translateX(-50%);z-index:9999;display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none; }
    .toast-float {
      background:rgba(18,30,20,.97);color:#fff;padding:13px 24px;border-radius:40px;
      border:1px solid rgba(200,168,75,.4);backdrop-filter:blur(12px);font-size:.9rem;
      opacity:0;transform:translateY(14px) scale(.95);
      transition:opacity .3s,transform .35s cubic-bezier(.34,1.56,.64,1);white-space:nowrap;
    }
    .toast-float.show { opacity:1;transform:translateY(0) scale(1); }

    /* ── Cursor hover enlargement ── */
    #cursorFollower.hover { transform:translate(-50%,-50%) scale(1.7) !important;opacity:.6; }

    /* ── Wishlist active ── */
    .product-wish.active { color:#e04444 !important; }

    /* ── Burger → X animation (3 lines become X when .open) ── */
    .burger.open span:nth-child(1) { transform:translateY(8px) rotate(45deg); }
    .burger.open span:nth-child(2) { opacity:0; }
    .burger.open span:nth-child(3) { transform:translateY(-8px) rotate(-45deg); }
  `;
  const s = document.createElement('style');
  s.textContent = css;
  document.head.appendChild(s);
})();
